import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTable, CellProps, Column, Cell, Row, HeaderGroup } from 'react-table'

import Box from '@mui/material/Box'
import Chip, { ChipProps } from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'

import LaunchIcon from '@mui/icons-material/Launch'

import CalendarIcon from '@/components/ui/icons/Calendar'
import ChipButton from '@/components/ui/buttons/ChipButton'
import DoctorIcon from '@/components/ui/icons/Doctor'
import PatientIcon from '@/components/ui/icons/Patient'
import Pagination from '@/components/ui/pagination/Pagination'

import { Expertise } from '@/core/models/expertise.model'

import { adapterLocaleMap } from '@/utils/locale'
import ErrorBoundary from '@/utils/ErrorBoundary'
import { AmericanFormattedDate } from '@/components/ui/date/AmericanFormattedDate'
import { useIsUS } from '@/hooks/useIsUS'

type ExpertisesTableProps = {
  data: Expertise[]
  isDoctor?: boolean
  onRowsPerPageChange?: (rowsPerPage: number) => void
  onPageChange?: (newPage: number) => void
  rowsPerPageOptions?: number[]
  totalCount: number
}

const defaultRowsPerPageOptions = [5, 10, 15]

export default function ExpertisesTable(props: ExpertisesTableProps) {
  const { data, isDoctor, onPageChange, onRowsPerPageChange, rowsPerPageOptions = defaultRowsPerPageOptions, totalCount } = props
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const isUS = useIsUS()

  const language = i18n.language.split('-')[0]

  const columns = useMemo(() => {
    return [
      {
        Header: t('page.expertises.table.header.doctor'),
        accessor: 'medecin',
        Cell: ({ value: medecin }: CellProps<Expertise, Expertise['medecin']>) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <DoctorIcon isFemale={medecin?.userInfo.gender === 'F'} />
            <span>{_.trim(`${medecin?.userInfo.name || ''} ${medecin?.userInfo.surname || ''}`)}</span>
          </Stack>
        ),
      },
      {
        Header: t('page.expertises.table.header.patient'),
        accessor: 'patient',
        Cell: ({ value: patient }: CellProps<Expertise, Expertise['patient']>) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <PatientIcon isFemale={patient?.userInfo.gender === 'F'} />
            <span>{_.trim(`${patient?.userInfo.name || ''} ${patient?.userInfo.surname || ''}`)}</span>
          </Stack>
        ),
      },
      {
        Header: t('page.expertises.table.header.startDate'),
        accessor: 'startDate',
        Cell: ({ value }: CellProps<Expertise, Expertise['startDate']>) => (
          <Stack alignItems="center" justifyContent="start" direction="row" spacing={1}>
            <CalendarIcon fontSize="small" />
            <Chip
              color="secondary"
              label={
                isUS ? (
                  <Stack component="span" direction="row" spacing={1}>
                    <AmericanFormattedDate date={value} />
                  </Stack>
                ) : (
                  <Stack component="span" direction="row" spacing={1}>
                    <span>
                      {/* Please make sure to add all relevant locale to the map, otherwise it crashes the app */}
                      {dayjs(value).isSame(dayjs(), 'day') ? dayjs(value).locale(adapterLocaleMap[language]).calendar() : dayjs(value).format('DD/MM/YYYY')}
                    </span>
                    <span>-</span>
                    <b>{dayjs(value).format('HH[h]mm')}</b>
                  </Stack>
                )
              }
              variant="filled"
            />
          </Stack>
        ),
      },
      {
        Header: t('page.expertises.table.header.status'),
        accessor: 'status',
        Cell: ({ value }: CellProps<Expertise, Expertise['status']>) => {
          let color: ChipProps['color']
          switch (value) {
            case 'draft':
              color = 'secondary'
              break
            case 'sent':
              color = 'success'
              break
            case 'closed':
              color = 'warning'
              break
          }
          return (
            <Chip
              color={color}
              label={t(`common:model.expertise.status.${value}.label`, { context: isDoctor ? 'doctor' : 'pds' })}
              size="small"
              variant="filled"
            />
          )
        },
      },
    ] as Column<Expertise>[]
  }, [isDoctor, language, t])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<Expertise>({
    columns,
    data,
  })

  const [page, setPage] = useState<number>(1)

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    onPageChange?.(newPage)
  }

  const handleOpenExpertise = (row: Row<Expertise>) => () => {
    const expertiseId = row.original._id
    navigate(`/expertises/${expertiseId}`)
  }

  return (
    <ErrorBoundary>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label={t('expertise:table.title', { defaultValue: 'Liste des télé-expertises' }) as string}>
          <TableHead>
            {headerGroups.map((headerGroup: HeaderGroup<Expertise>) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: HeaderGroup<Expertise>) => {
                  return (
                    <TableCell {...column.getHeaderProps()} scope="col">
                      {column.render('Header')}
                    </TableCell>
                  )
                })}
                <TableCell align="right">{t('common:table.header.action')}</TableCell>
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row)
              return (
                <TableRow {...row.getRowProps()} key={row.original._id}>
                  {row.cells.map((cell: Cell<Expertise>) => (
                    <TableCell {...cell.getCellProps()}>{cell.render('Cell')}</TableCell>
                  ))}
                  <TableCell key={`cell_${row.index}_actions`} align="center">
                    <Stack justifyContent="flex-end" direction="row" spacing={1}>
                      <Tooltip title={t('page.expertises.openExpertiseButton.tooltip')}>
                        <ChipButton color="info" onClick={handleOpenExpertise(row)}>
                          <LaunchIcon />
                        </ChipButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
          <Pagination
            color="primary"
            itemsPerPageOptions={rowsPerPageOptions}
            shape="rounded"
            page={page}
            onPageChange={handleChangePage}
            onItemsPerPageChange={onRowsPerPageChange}
            totalItems={totalCount}
          />
        </Box>
      </TableContainer>
    </ErrorBoundary>
  )
}
